const DEFAULT_USD_FORMATTER = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function dollarsToCents(dollars: number): number {
  return Math.round((dollars + Number.EPSILON) * 100);
}

/**
 * Formats an integer cents amount as a USD currency string (e.g. 90000 -> "$900.00").
 * A NaN input formats as "$0.00". Pass `fractionDigits` to fix the number of decimal
 * places (both minimum and maximum); it defaults to USD's standard two.
 */
export function formatCentsToUsd(cents: number, fractionDigits?: number): string {
  const dollars = Number.isNaN(cents) ? 0 : cents / 100;

  if (fractionDigits == null) {
    return DEFAULT_USD_FORMATTER.format(dollars);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(dollars);
}

const UNIT_RATE_FORMATTER = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });

/**
 * A per-unit overage rate, given as a dollar string ("0.004"), formatted as a price ("$0.004").
 * Returns null when the rate is missing or not a positive number.
 *
 * `Intl` currency formatting fixes two decimals, which rounds a sub-cent rate to "$0.00". A per-profile
 * rate is smaller than a cent, so the figure carries more than currency's default precision.
 */
export function formatUnitRate(rate: string | undefined): string | null {
  const dollars = Number(rate);
  if (!Number.isFinite(dollars) || dollars <= 0) {
    return null;
  }

  return `$${UNIT_RATE_FORMATTER.format(dollars)}`;
}

export type TaxedTotal = {
  totalPriceCents: number;
  taxAmountCents: number;
  totalWithTaxCents: number;
};

export function withTax(subtotalCents: number, taxRateFraction?: number): TaxedTotal {
  const taxAmountCents = taxRateFraction == null ? 0 : Math.round(subtotalCents * taxRateFraction);
  return { totalPriceCents: subtotalCents, taxAmountCents, totalWithTaxCents: subtotalCents + taxAmountCents };
}
